/**
 * Fullscreen media viewer for the product gallery.
 *
 * Tapping a gallery image opens a fullscreen dialog at that image. Swiping is
 * handled by native horizontal scroll-snap rather than pointer maths, which keeps
 * the gesture at the platform's own frame rate and gives correct rubber-banding,
 * momentum and RTL behaviour for free.
 *
 * Progressive enhancement: with no JS the gallery keeps working as before.
 */
class MediaLightbox extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.track = this.querySelector('[data-lightbox-track]');
    if (!this.dialog || !this.track) return;

    this.slides = Array.from(this.track.querySelectorAll('[data-lightbox-slide]'));
    this.counter = this.querySelector('[data-lightbox-counter]');
    this.index = 0;

    this.querySelector('[data-lightbox-prev]')?.addEventListener('click', () => this.step(-1));
    this.querySelector('[data-lightbox-next]')?.addEventListener('click', () => this.step(1));
    this.querySelectorAll('[data-lightbox-close]').forEach((button) => button.addEventListener('click', () => this.close()));

    // Tapping the backdrop area closes, but never a swipe that ends on an image.
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog || event.target === this.track) this.close();
    });

    this.dialog.addEventListener('cancel', (event) => { event.preventDefault(); this.close(); });
    this.dialog.addEventListener('keydown', (event) => this.onKeydown(event));
    this.dialog.addEventListener('close', () => {
      document.documentElement.classList.remove('dialog-open');
      this.opener?.focus?.();
    });

    // Keep the counter and arrow state in step with wherever the swipe settles.
    let settle;
    this.track.addEventListener('scroll', () => {
      clearTimeout(settle);
      settle = setTimeout(() => this.syncFromScroll(), 80);
    }, { passive: true });
  }

  get reduceMotion() {
    return document.documentElement.dataset.motion === 'none'
      || matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  open(index, opener) {
    if (!this.dialog || this.dialog.open) return;
    this.opener = opener;
    document.documentElement.classList.add('dialog-open');
    this.dialog.showModal();
    // Jump without animation on open, so the first frame is already correct.
    this.goTo(index, 'auto');
    requestAnimationFrame(() => this.goTo(index, 'auto'));
  }

  close() {
    if (!this.dialog?.open) return;
    this.dialog.close();
  }

  goTo(index, behavior) {
    const total = this.slides.length;
    if (!total) return;
    this.index = Math.max(0, Math.min(total - 1, index));
    const slide = this.slides[this.index];
    this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft, behavior: behavior || (this.reduceMotion ? 'auto' : 'smooth') });
    this.updateChrome();
  }

  step(delta) {
    this.goTo(this.index + delta);
  }

  /** Nearest slide to the track centre wins, matching what the visitor sees. */
  syncFromScroll() {
    if (!this.slides.length) return;
    const centre = this.track.scrollLeft + this.track.clientWidth / 2;
    let best = 0;
    let bestDistance = Infinity;
    this.slides.forEach((slide, i) => {
      const distance = Math.abs(slide.offsetLeft - this.track.offsetLeft + slide.offsetWidth / 2 - centre);
      if (distance < bestDistance) { bestDistance = distance; best = i; }
    });
    this.index = best;
    this.updateChrome();
  }

  updateChrome() {
    if (this.counter) this.counter.textContent = `${this.index + 1} / ${this.slides.length}`;
    const prev = this.querySelector('[data-lightbox-prev]');
    const next = this.querySelector('[data-lightbox-next]');
    if (prev) prev.disabled = this.index === 0;
    if (next) next.disabled = this.index === this.slides.length - 1;
    this.slides.forEach((slide, i) => slide.classList.toggle('is-current', i === this.index));
  }

  onKeydown(event) {
    if (event.key === 'ArrowRight') { event.preventDefault(); this.step(1); }
    if (event.key === 'ArrowLeft') { event.preventDefault(); this.step(-1); }
    if (event.key === 'Home') { event.preventDefault(); this.goTo(0); }
    if (event.key === 'End') { event.preventDefault(); this.goTo(this.slides.length - 1); }
  }
}

if (!customElements.get('media-lightbox')) customElements.define('media-lightbox', MediaLightbox);

/**
 * Opens the lightbox from the gallery. Images only: video, model and external
 * embeds keep their own inline controls, which a fullscreen swipe would fight.
 */
document.addEventListener('click', (event) => {
  const figure = event.target.closest?.('.product-gallery__item');
  if (!figure || figure.dataset.mediaType !== 'image') return;
  if (event.target.closest('button, a')) return;

  const gallery = figure.closest('product-media');
  const lightbox = gallery?.querySelector('media-lightbox');
  if (!lightbox || typeof lightbox.open !== 'function') return;
  if (!lightbox.dataset.enabled) return;

  // Desktop keeps the click-through-to-zoom-free grid unless opted in.
  if (lightbox.dataset.enabled === 'mobile' && !matchMedia('(max-width: 47.99rem)').matches) return;

  event.preventDefault();
  const index = Number(figure.dataset.mediaIndex || 1) - 1;
  lightbox.open(index, figure.querySelector('img') || figure);
});
